/**
 * Pure helper functions extracted from game.js.
 * None of these reference module-level state — all data is passed as parameters.
 */
import { t } from "./i18n.js";
import { OPPONENT_BACK, PETROL_BACK, SUIT_COLORS } from "./constants.js";

// --- DOM factories ---

/** Schedule a one-shot callback in an idle window — requestIdleCallback where
 *  available, else a same-tick setTimeout fallback (no rIC on Safari < 15.4).
 *  Lifted here from game-start-ui.js (tbt-reshaping slice 1 §4) once a third
 *  consumer (card-template-cache.js) joined game-start-ui.js and game-graph.js,
 *  past locality's single-file threshold. NOT game.js's separate idle helper
 *  (a 250ms fallback backoff for its result-surface prefetch re-arm while the
 *  animation lock is held) — different knowledge, deliberately not reconciled. */
export function scheduleIdle(fn) {
  if (typeof requestIdleCallback === "function") requestIdleCallback(fn);
  else setTimeout(fn, 0);
}

/** Creates a div element with the given class name(s). */
export function div(className) {
  const el = document.createElement("div");
  el.className = className;
  return el;
}

/** Creates a span element. If style contains ':', it's applied as cssText; otherwise as className. */
export function span(text, style) {
  const el = document.createElement("span");
  el.textContent = text;
  if (style && !style.includes(":")) {
    el.className = style;
  } else if (style) {
    el.style.cssText = style;
  }
  return el;
}

/** Creates a button element with class 'btn ' + className. */
export function btn(text, className, onClick) {
  const el = document.createElement("button");
  el.className = "btn " + className;
  el.textContent = text;
  if (onClick) el.addEventListener("click", onClick);
  return el;
}

// --- Formatters & translators ---

/** Translates a game position (FOREHAND, MIDDLEHAND, DEALER) via i18n. */
export function posName(position) {
  return t("position" + position) || position;
}

/** Maps a suit name to its display color (hex). Every call site passes a real suit name —
 *  Grand's bid accent is special-cased to its own lighter violet before it would ever reach
 *  here (render/position-indicator.js#bidAccentColor), so this deliberately has no "grand"
 *  entry and no unmapped-suit fallback: a suit name this can't map is a caller bug, better
 *  surfaced as a visibly broken render (an `undefined` fill) than silently absorbed into a
 *  plausible-looking neutral color. */
export function suitColor(suit) {
  return SUIT_COLORS[suit];
}

/** Suit display order — diamonds, hearts, spades, clubs (src/main/java/CLAUDE.md § Suit
 *  display order). Single home for a list rules-enhance.js and help-enhance.js each declared
 *  independently. */
export const SUIT_DISPLAY_ORDER = ["diamonds", "hearts", "spades", "clubs"];

// --- Game logic helpers ---

/** Checks whether a trick card is trump given the current game state. */
export function isTrumpCard(tc, state) {
  if (!state.gameType) return false;
  if (state.gameType === "NULL") return false; // Null has no trump
  if (tc.rank === "jack") return true; // Jacks are always trump in suit/grand/ramsch
  if (state.gameType === "GRAND" || state.gameType === "RAMSCH") return false; // only jacks
  return tc.suit === state.gameType.toLowerCase(); // suit game
}

/**
 * Returns the alliance type for a position from the spectated player's perspective.
 * Returns 'enemy', 'friend', 'self', or null.
 */
export function getPositionAlliance(positionName, state) {
  const spectated = spectatedPlayer(state);
  if (positionName === spectated?.position) return "self";
  if (state.isRamsch) return "enemy";
  if (!state.declarer) return null;
  if (spectated?.isDeclarer) return "enemy";
  return positionName === state.declarer ? "enemy" : "friend";
}

/**
 * True iff the spectated seat is the one the server is actually waiting on
 * (state.awaitedPosition, GameStateDto — a global game fact, same for every subscriber). The
 * single gate every input-affordance arming site checks before offering a bid/discard/game-type/
 * card-play prompt — solo always resolves true at an input pause (the awaited human is always the
 * viewer there); a multiplayer subscriber whose seat isn't awaited, or a commentator/autoPlay
 * view (no spectated seat), resolves false (debug-first-multiplayer-issues).
 */
export function isViewerTurn(state) {
  return (
    state?.awaitedPosition != null &&
    spectatedPlayer(state)?.position === state.awaitedPosition
  );
}

// --- Screen position mapping ---

/** The player state.spectatedSeat identifies — null in spectator/commentator mode, or
 *  when state itself is absent (the pre-game-start layout seed has no state yet). */
export function spectatedPlayer(state) {
  return state?.players?.find((p) => p.seat === state.spectatedSeat) ?? null;
}

const POSITIONS = ["FOREHAND", "MIDDLEHAND", "DEALER"];

/**
 * Clockwise play order starting from `lead` — mirrors the server's Position#playOrder (the
 * model this once lived in, before the seat-keyed-players reshape moved screen placement here).
 */
function playOrderFrom(lead) {
  const start = POSITIONS.indexOf(lead);
  return [0, 1, 2].map((offset) => POSITIONS[(start + offset) % 3]);
}

/**
 * The seat→screen-slot mapping, wholly client-owned since the seat-keyed-players reshape (the
 * server used to compute this via GameStateDto#mapScreenPositions). The spectated player's
 * position anchors "human" (top); the next two positions in clockwise play order fill
 * "left"/"right". No spectated seat AMONG A REAL ROUND (commentator mode — players present,
 * spectatedSeat null) falls back to the same MIDDLEHAND/FOREHAND/DEALER order the server used.
 * No players at all (no-session state, or a caller's minimal rig) resolves every slot to null —
 * there is no round to place seats in.
 */
function screenOrderFor(state) {
  if (!state?.players?.length) return [null, null, null];
  const spectated = spectatedPlayer(state);
  if (!spectated) return ["MIDDLEHAND", "FOREHAND", "DEALER"];
  return playOrderFrom(spectated.position);
}

/** Maps a game position (FOREHAND/MIDDLEHAND/DEALER) to a screen position (left/right/human). */
export function screenPositionFor(gamePosition, state) {
  const [human, left, right] = screenOrderFor(state);
  if (gamePosition === human) return "human";
  if (gamePosition === left) return "left";
  if (gamePosition === right) return "right";
  return null;
}

/**
 * The PlayerDto occupying a screen slot ("human"/"left"/"right") — the reverse lookup of
 * screenPositionFor, replacing the server's former topPlayer/bottomLeftPlayer/bottomRightPlayer
 * fields. Null when state carries no players (sparse/no-session state).
 */
export function playerForScreenSlot(state, slot) {
  const [human, left, right] = screenOrderFor(state);
  const position = slot === "human" ? human : slot === "left" ? left : right;
  return (state?.players ?? []).find((p) => p.position === position) ?? null;
}

/**
 * The card-back color a screen seat owns: an opponent's identity back, or the
 * neutral petrol back where no single player owns the cards (the human seat, who
 * has no card-back identity, and null). Single source for the seat→back mapping
 * the trick piles and the travelling skat pile share.
 */
export function backColorForSeat(screenSeat) {
  if (screenSeat === "left") return OPPONENT_BACK.LEFT;
  if (screenSeat === "right") return OPPONENT_BACK.RIGHT;
  return PETROL_BACK;
}
